import { useDropzone } from 'react-dropzone';
import { useFormikContext } from 'formik';
import cloudIcon from '@shared/assets/svgs/cloud-icon.svg';
import { cn } from '@src/design-system/helpers';
import { Label } from '@src/design-system/label';
import { showToast } from '@src/shared/utils/helpers/show-toast';
import {
  ANNOUNCEMENT_IMAGE_ACCEPT,
  ANNOUNCEMENT_MAX_FILE_SIZE_MB,
  ANNOUNCEMENT_VIDEO_ACCEPT,
  validateAnnouncementMedia,
} from '../../../utils/upload-announcement-media';
import type { TAnnouncementFormValues } from '../schema';
import { AnnouncementSectionTitle } from './announcement-section-title';

export function AnnouncementMediaSection() {
  const { values, setFieldValue } = useFormikContext<TAnnouncementFormValues>();

  const accept =
    values.mediaType === 'video'
      ? ANNOUNCEMENT_VIDEO_ACCEPT
      : ANNOUNCEMENT_IMAGE_ACCEPT;

  const onDrop = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    const error = validateAnnouncementMedia(file);
    if (error) {
      showToast({
        title: 'Invalid file',
        description: error,
        variant: 'error',
      });
      return;
    }

    const isVideo = file.type.startsWith('video/');
    if (
      (values.mediaType === 'image' && isVideo) ||
      (values.mediaType === 'video' && !isVideo)
    ) {
      showToast({
        title: 'Invalid file',
        description: `Please upload a ${values.mediaType} file.`,
        variant: 'error',
      });
      return;
    }

    setFieldValue('mediaFile', file);
    setFieldValue('existingMedia', null);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept,
  });

  const selectedFileName =
    values.mediaFile?.name ?? values.existingMedia?.name ?? null;

  return (
    <section className='flex w-full flex-col gap-4 rounded-xl border border-primary-100 bg-white p-6'>
      <AnnouncementSectionTitle title='2. Media' />

      <div className='flex gap-2'>
        {(['image', 'video'] as const).map((type) => (
          <button
            key={type}
            type='button'
            onClick={() => {
              setFieldValue('mediaType', type);
              setFieldValue('mediaFile', null);
              if (
                values.existingMedia &&
                ((type === 'image' &&
                  values.existingMedia.mime_type.startsWith('video/')) ||
                  (type === 'video' &&
                    values.existingMedia.mime_type.startsWith('image/')))
              ) {
                setFieldValue('existingMedia', null);
              }
            }}
            className={cn(
              'h-12 rounded-full border px-3 text-sm capitalize text-gray-800',
              values.mediaType === type
                ? 'border-primary-1000 bg-primary-100'
                : 'border-primary-200 bg-transparent'
            )}
          >
            {type}
          </button>
        ))}
      </div>

      <div className='flex flex-col gap-2'>
        <Label>Media</Label>
        <div
          {...getRootProps()}
          className={cn(
            'flex h-36 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-primary-200 bg-primary-30 px-4',
            isDragActive && 'border-primary-1000 bg-primary-100'
          )}
        >
          <input {...getInputProps()} />
          <img
            alt='cloud icon'
            src={cloudIcon}
            className='!h-8 !w-8'
          />
          <p className='mt-2 text-center text-sm text-gray-800'>
            <span className='font-semibold'>Click to upload</span> or drag and
            drop
          </p>
          <p className='mt-1 text-center text-xs text-gray-600'>
            Supported formats: JPG, PNG, WEBP, MP4, MOV (MAX.{' '}
            {ANNOUNCEMENT_MAX_FILE_SIZE_MB} MB)
          </p>
          {selectedFileName ? (
            <p className='mt-2 truncate text-xs font-medium text-primary-1000'>
              {selectedFileName}
            </p>
          ) : null}
        </div>
        <p className='text-sm text-gray-600'>
          Recommended aspect ratio: 9:16 (Portrait)
        </p>
      </div>
    </section>
  );
}
